/**
 * Zustand store for ivy-chat state.
 * Includes session persistence (localStorage) and reconnection support.
 */

import { create } from 'zustand';
import type { ChatSession } from '@/services/ivyChatApi';
import type { UIMessage } from '@/lib/ivyChat/messageParser';
import { processSDKMessage, clearMessageParserCache } from '@/lib/ivyChat/messageParser';
import * as api from '@/services/ivyChatApi';
import { readPaywall402, withPaywallCards, outOfCreditsMessage } from '@/services/billingPaywallDetector';
import { isImageReadNote, refusalMessage, storedFailureMessage, streamErrorMessage } from '@/lib/ivyChat/chatNotices';

const ACTIVE_SESSION_KEY = 'ivy_chat_active_session';
const LAST_EVENT_INDEX_KEY = 'ivy_chat_last_event_index';

// Module-level abort controller — cancelled when switching sessions to stop stale stream readers
let activeStreamAbort: AbortController | null = null;

function cancelActiveStream() {
  if (activeStreamAbort) {
    activeStreamAbort.abort();
    activeStreamAbort = null;
  }
}

interface IvyChatState {
  sessions: ChatSession[];
  activeChatId: string | null;
  sessionsLoading: boolean;

  messages: UIMessage[];
  isStreaming: boolean;
  currentRequestId: string | null;
  eventIndex: number;

  loadSessions: () => Promise<void>;
  createSession: (title?: string) => Promise<ChatSession>;
  selectSession: (chatId: string) => Promise<void>;
  deleteSession: (chatId: string) => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  abortStream: () => Promise<void>;
  checkAndReconnect: (chatId: string) => Promise<void>;
}

function processNdjsonStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  ownerChatId: string,
  set: (fn: (state: IvyChatState) => Partial<IvyChatState>) => void,
  get: () => IvyChatState,
  signal: AbortSignal,
) {
  const decoder = new TextDecoder();
  let buffer = '';
  let lastEventTime = Date.now();
  let madeWork = false;

  // Inactivity watchdog — if no events arrive for 30s, check server stream status
  // and break the pump if the stream is no longer active. Prevents permanent
  // "Thinking..." state when the HTTP stream hangs (proxy keep-alive, network issues).
  const INACTIVITY_TIMEOUT_MS = 30_000;
  let inactivityTimer: ReturnType<typeof setInterval> | null = null;

  function startInactivityWatchdog() {
    inactivityTimer = setInterval(async () => {
      if (Date.now() - lastEventTime > INACTIVITY_TIMEOUT_MS) {
        try {
          const status = await api.getStreamStatus(ownerChatId);
          if (!status.active) {
            console.warn('[ivyChat] Stream inactive on server — forcing pump exit');
            reader.cancel().catch(() => {});
          }
        } catch {
          reader.cancel().catch(() => {});
        }
      }
    }, 10_000);
  }

  function stopInactivityWatchdog() {
    if (inactivityTimer) {
      clearInterval(inactivityTimer);
      inactivityTimer = null;
    }
  }

  async function pump(): Promise<void> {
    startInactivityWatchdog();
    try {
      while (true) {
        if (signal.aborted || get().activeChatId !== ownerChatId) {
          reader.cancel().catch(() => {});
          return;
        }

        const { done, value } = await reader.read();
        if (done) break;

        lastEventTime = Date.now();

        if (signal.aborted || get().activeChatId !== ownerChatId) {
          reader.cancel().catch(() => {});
          return;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let receivedDone = false;
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const parsed = JSON.parse(line);

            // Backend sends { type: 'done' } when the Claude process exits.
            // Break immediately rather than waiting for HTTP stream EOF.
            if (parsed.type === 'done') {
              receivedDone = true;
              break;
            }

            if (parsed.type === 'claude_json' && parsed.data) {
              const uiMessages = processSDKMessage(parsed.data as Record<string, unknown>);
              if (uiMessages.some((m) => m.type === 'chat' || m.type === 'tool_use')) madeWork = true;
              if (uiMessages.length > 0) {
                set(state => {
                  const newIndex = state.eventIndex + 1;
                  localStorage.setItem(LAST_EVENT_INDEX_KEY, String(newIndex));
                  return {
                    messages: [...state.messages, ...withPaywallCards(state.messages, uiMessages)],
                    eventIndex: newIndex,
                  };
                });
              }
            } else if (parsed.type === 'error') {
              set(state => ({
                messages: [...state.messages, {
                  id: `msg-error-${Date.now()}`,
                  type: 'system' as const,
                  content: streamErrorMessage(parsed, madeWork),
                  timestamp: Date.now(),
                  subtype: 'error',
                }],
              }));
            }
          } catch {
            // Skip malformed NDJSON
          }
        }

        if (receivedDone) break;
      }
    } finally {
      stopInactivityWatchdog();
    }
  }

  return pump();
}

export const useIvyChatStore = create<IvyChatState>((set, get) => ({
  sessions: [],
  activeChatId: null,
  sessionsLoading: false,
  messages: [],
  isStreaming: false,
  currentRequestId: null,
  eventIndex: 0,

  loadSessions: async () => {
    set({ sessionsLoading: true });
    try {
      const sessions = await api.listSessions();
      set({ sessions, sessionsLoading: false });
    } catch {
      set({ sessionsLoading: false });
    }
  },

  createSession: async (title?: string) => {
    const session = await api.createSession(title);
    set(state => ({
      sessions: [session, ...state.sessions],
      activeChatId: session.chatId,
      messages: [],
      eventIndex: 0,
    }));
    localStorage.setItem(ACTIVE_SESSION_KEY, session.chatId);
    localStorage.setItem(LAST_EVENT_INDEX_KEY, '0');
    clearMessageParserCache();
    return session;
  },

  selectSession: async (chatId: string) => {
    cancelActiveStream();
    set({ activeChatId: chatId, messages: [], isStreaming: false, eventIndex: 0 });
    localStorage.setItem(ACTIVE_SESSION_KEY, chatId);
    localStorage.setItem(LAST_EVENT_INDEX_KEY, '0');
    clearMessageParserCache();

    try {
      // Load messages from DB (via API Server, not Fargate)
      const dbMessages = await api.getMessages(chatId);
      // Filter sub-agent prompts that leaked into DB
      const isSubAgentPrompt = (t: string) =>
        t.length >= 400 && (
          /\/tmp\/\.ivy\/[a-f0-9-]{20,}\/slides\/slide-\d{3}/.test(t) ||
          /(?:Redesign|Build|Edit|Generate|Create|Update|Fix)\s+slide-\d{3}\s+for\s+project\s+[a-f0-9]{8}-/i.test(t) ||
          ['## What to change', '## Files to read', '## Files to write', '## Critical rules'].filter(h => t.includes(h)).length >= 2
        );
      const uiMessages: UIMessage[] = dbMessages.filter(m =>
        !(m.role === 'user' && (isSubAgentPrompt(m.content) || isImageReadNote(m.content)))
      ).map((m, i) => storedFailureMessage(m, `msg-${i}`) ?? ({
        id: m.id || `msg-${i}`,
        type: m.role === 'user' ? 'chat' as const
          : m.role === 'assistant' ? 'chat' as const
          : m.role === 'tool_use' ? 'tool_use' as const
          : m.role === 'tool_result' ? 'tool_result' as const
          : m.role === 'result' ? 'result' as const
          : 'system' as const,
        role: (m.role === 'user' ? 'user' : m.role === 'assistant' ? 'assistant' : undefined) as any,
        content: m.content,
        timestamp: new Date(m.createdAt).getTime(),
        toolName: m.toolName || undefined,
        toolInput: m.toolInput || undefined,
        toolUseId: m.toolUseId || undefined,
        isError: m.isError,
        costUsd: m.costUsd || undefined,
        durationMs: m.durationMs || undefined,
        inputTokens: m.inputTokens || undefined,
        outputTokens: m.outputTokens || undefined,
      }));
      set({ messages: withPaywallCards([], uiMessages), eventIndex: uiMessages.length });
    } catch {
      // No messages yet
    }
  },

  deleteSession: async (chatId: string) => {
    await api.deleteSession(chatId);
    set(state => {
      const sessions = state.sessions.filter(s => s.chatId !== chatId);
      const activeChatId = state.activeChatId === chatId ? null : state.activeChatId;
      if (state.activeChatId === chatId) {
        cancelActiveStream();
        localStorage.removeItem(ACTIVE_SESSION_KEY);
      }
      return {
        sessions,
        activeChatId,
        messages: activeChatId ? state.messages : [],
      };
    });
  },

  sendMessage: async (message: string) => {
    if (get().isStreaming) return;

    // Auto-create a session if none exists or current one is stale
    let { activeChatId } = get();
    if (!activeChatId) {
      try {
        const session = await get().createSession();
        activeChatId = session.chatId;
      } catch (err) {
        console.error('[ivy-chat] Failed to create session:', err);
        return;
      }
    }

    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const userMessage: UIMessage = {
      id: `msg-user-${Date.now()}`,
      type: 'chat',
      role: 'user',
      content: message,
      timestamp: Date.now(),
    };

    set(state => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      currentRequestId: requestId,
    }));

    cancelActiveStream();
    const abort = new AbortController();
    activeStreamAbort = abort;

    try {
      let res = await api.sendChatMessage(activeChatId, message, requestId);

      // Handle stale session (404) — create new session and retry
      if (res.status === 404) {
        console.warn('[ivy-chat] Session not found, creating new one');
        const session = await get().createSession();
        activeChatId = session.chatId;
        res = await api.sendChatMessage(activeChatId, message, requestId);
      }

      if (res.status === 402) {
        const paywall = await readPaywall402(res);
        if (paywall) set(state => ({ messages: [...state.messages, outOfCreditsMessage(paywall)] }));
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        set(state => ({
          messages: [...state.messages, {
            id: `msg-error-${Date.now()}`,
            type: 'system' as const,
            content: refusalMessage(res.status, body, res.headers.get('Retry-After')),
            timestamp: Date.now(),
            subtype: 'error',
          }],
        }));
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) return;

      await processNdjsonStream(reader, activeChatId, set, get, abort.signal);
      get().loadSessions();
    } catch (err) {
      if ((err as Error).name !== 'AbortError' && get().activeChatId === activeChatId) {
        console.warn('[ivyChat] Stream broke, attempting reconnect:', (err as Error).message);
        try {
          const status = await api.getStreamStatus(activeChatId);
          if (status.active) {
            console.log('[ivyChat] Backend stream still active, reconnecting...');
            const reconnAbort = new AbortController();
            activeStreamAbort = reconnAbort;
            const reconnRes = await api.reconnectToStream(activeChatId, get().eventIndex);
            const reconnReader = reconnRes.body?.getReader();
            if (reconnReader) {
              await processNdjsonStream(reconnReader, activeChatId, set, get, reconnAbort.signal);
              console.log('[ivyChat] Reconnect stream completed');
            }
          } else {
            console.log('[ivyChat] Backend stream done, loading messages from DB');
            await get().selectSession(activeChatId);
          }
        } catch {
          try {
            await get().selectSession(activeChatId);
          } catch {
            set(state => ({
              messages: [...state.messages, {
                id: `msg-error-${Date.now()}`,
                type: 'system' as const,
                content: 'Connection lost. Please refresh if messages are missing.',
                timestamp: Date.now(),
                subtype: 'error',
              }],
            }));
          }
        }
      }
    } finally {
      if (get().activeChatId === activeChatId) {
        set({ isStreaming: false, currentRequestId: null });
      }
    }
  },

  abortStream: async () => {
    const { currentRequestId } = get();
    if (currentRequestId) {
      cancelActiveStream();
      await api.abortRequest(currentRequestId);
      set({ isStreaming: false, currentRequestId: null });
    }
  },

  checkAndReconnect: async (chatId: string) => {
    if (get().isStreaming) return;
    try {
      const status = await api.getStreamStatus(chatId);
      if (get().isStreaming) return;
      if (get().activeChatId !== chatId) return;
      if (status.active) {
        cancelActiveStream();
        const abort = new AbortController();
        activeStreamAbort = abort;

        const sinceIndex = parseInt(localStorage.getItem(LAST_EVENT_INDEX_KEY) || '0', 10);
        set({ isStreaming: true });

        const res = await api.reconnectToStream(chatId, sinceIndex);
        const reader = res.body?.getReader();
        if (!reader) {
          set({ isStreaming: false });
          return;
        }

        await processNdjsonStream(reader, chatId, set, get, abort.signal);
        get().loadSessions();
        if (get().activeChatId === chatId) {
          set({ isStreaming: false });
        }
      }
    } catch {
      // No active stream — history already loaded via selectSession
    }
  },
}));

/** Get the last active session ID from localStorage. */
export function getPersistedActiveSession(): string | null {
  return localStorage.getItem(ACTIVE_SESSION_KEY);
}
